import { z } from 'zod';
import { VENUES } from '../../ontology/calendar.ts';
import { STALE_REASONS } from '../../ontology/objects/indicated.ts';
import { instant, price } from './primitives.ts';

const tracePointSchema = z.object({ at: instant, price: z.number(), reference: z.number() });

const VERDICT_KINDS = ['through-stop', 'past-target', 'profit', 'against', 'flat'] as const;

export const verdictSchema = z.object({
  kind: z.enum(VERDICT_KINDS),
  r: z.number().nullable(),
  beyond: z.number().nullable(),
  said: z.string(),
});

/** Deliberately no colour or direction field: an indication is not a tick. */
export const indicatedOpenSchema = z.object({
  venue: z.enum(VENUES),
  contractToken: z.number().int().positive(),
  tradingsymbol: z.string(),
  commodity: z.string(),
  close: price,
  price,
  change: z.number(),
  changeFraction: z.number(),
  reference: z.object({
    symbol: z.string(),
    unit: z.string(),
    price: z.number(),
    at: instant,
    atClose: z.number(),
    said: z.string(),
  }),
  stale: z.enum(STALE_REASONS).nullable(),
  late: z.object({ tag: z.string(), said: z.string() }).nullable(),
  verdict: verdictSchema.nullable(),
  trace: z.array(tracePointSchema),
});

export type IndicatedOpen = z.infer<typeof indicatedOpenSchema>;

const nightSchema = z.object({
  venue: z.enum(VENUES),
  since: instant,
  until: instant,
  quiet: z.string().nullable(),
  cme: z.array(z.object({ commodity: z.string(), code: z.string(), close: z.number() })),
});

export type WireNight = z.infer<typeof nightSchema>;

/** `since` and `until` are MCX's night. */
export const indicationsSchema = z.object({
  indications: z.array(indicatedOpenSchema),
  since: instant.nullable(),
  until: instant.nullable(),
  nights: z.array(nightSchema),
});

export type WireIndications = z.infer<typeof indicationsSchema>;

export const NO_INDICATIONS: WireIndications = {
  indications: [],
  since: null,
  until: null,
  nights: [],
};
